import { useState } from "react";
import { createListStore, getFilterDto, getSort } from "../../common/listStore";
import { PageTitle } from "../../layout/PageTitle";
import { listHeaderItems, renderListStore } from "../../common/listStoreUi";
import { Button, Grid } from "@mui/material";
import { createEntityStore } from "../../common/entityStore";
import { today } from "../../common/utils";
import { propValidValue } from "../../common/entityStoreValidators";
import { propDateField } from "../../common/entityStoreForm";
import { downloadFromApiPost } from "../../common/download";
import { createAxios } from "../../common/axios";
import { Delete } from "@mui/icons-material";
import { useSimpleDialog, yesNoButtons } from "../../common/dialog";
import { applyListStoreFilters, nextDay } from "../../common/listStoreFilters";

interface PaymentErrorDto {
    id: number;
    payId: number;
    custId: string;
    invcId: string;
    payDte: string;
    payAmt: number;
    rsn: string;
    isBat: number;
}

interface PaymentErrorListFilters {
    from?: string;
    to?: string;
    fromFormatted?: string;
    toFormatted?: string;
}

function getFiltersStoreInitialData() {
    return { from: today(), to: today() };
}

export function PaymentErrorReportPage() {
    const [{ useStore, useFiltersStore }] = useState(() => {
        const useStore = createListStore<PaymentErrorDto>({
            path: "reports/paymentErrors/list",
            supportsApiFilterSortPage: true,
            itemsPerPage: 15,
            listProps: [
                { column: "custId", label: "Customer", width: 150 },
                { column: "invcId", label: "Invoice", width: 150 },
                { column: "payDte", label: "Pay date", width: 120, type: "date" },
                { column: "payAmt", label: "Pay amount", width: 120, type: "currency" },
                { column: "rsn", label: "Reject reason", flex: 1 },
            ]
        });
        const useFiltersStore = createEntityStore<PaymentErrorListFilters>({
            data: getFiltersStoreInitialData(), isValidationEnabled: true,
            propValidators: [
                propValidValue("from", "fromFormatted"),
                propValidValue("to", "toFormatted"),
            ]
        });
        applyListStoreFilters(useFiltersStore, useStore, [
            { listProps: ["payDte"], operator: "GreaterThanOrEqual", filterProp: "from" },
            { listProps: ["payDte"], operator: "LessThan", filterProp: "to", transform: nextDay },
        ], true);
        return { useStore, useFiltersStore };
    });

    function clearFiltersStore() {
        useFiltersStore.getState().setClientData(getFiltersStoreInitialData());
    }

    const { isFetching, selectedItem } = useStore(p => ({ isFetching: p.fetching, selectedItem: p.selectedItem }));

    const simpleDialog = useSimpleDialog();

    function deleteItem() {
        simpleDialog.show("Delete payment error", "Delete selected payment error?", undefined, yesNoButtons(async () => {
            try {
                await createAxios().delete(`reports/paymentErrors/${selectedItem!.id}`);
            } finally {
                useStore.getState().fetch();
            }
        }));
    }

    return <>
        {simpleDialog.render()}
        <PageTitle title="Payment errors" />
        {renderListStore(useStore, {
            filtersMaxWidth: "lg",
            header: listHeaderItems([
                ["remove", () => <Button color="delete" disabled={selectedItem == null} title="Delete" onClick={deleteItem}><Delete /></Button>],
                ["export", () => <Button disabled={isFetching} onClick={() => downloadFromApiPost(`reports/paymentErrors/xlsx`, {
                    filter: getFilterDto(useStore.getState()),
                    sort: getSort(useStore.getState()),
                })}>Export</Button>],
            ]),
            renderExternalFilters: () => <>
                <Grid item xs={3} md={2}>{propDateField(useFiltersStore)("from", "fromFormatted", "Date from")}</Grid>
                <Grid item xs={3} md={2}>{propDateField(useFiltersStore)("to", "toFormatted", "Date to")}</Grid>
            </>, clearExternalFilters: clearFiltersStore, instantSearch: true,
        })};
    </>
}
